import { PersonRow } from '@affine/core/components/identity-row';
import { useI18n } from '@affine/i18n';
import { ArrowDownSmallIcon } from '@blocksuite/icons/rc';
import { useCallback, useEffect, useState } from 'react';

import { SettingsConfirmButton } from '../shared/settings-content';
import { SettingsBadge, SettingsEntityCard } from '../shared/settings-entity';
import * as styles from './styles.css';

export interface McpToken {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string;
  userRole: string;
  workspaceId: string;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  expiresAt: string | null;
  maskedToken: string;
  toolPolicy: WorkspaceMcpToolPolicy;
}

export type WorkspaceMcpToolPolicy = {
  disabledCategories: string[];
  disabledTools: string[];
};

export type McpAvailableTool = {
  name: string;
  category: string;
  description: string;
  requiredPermission?: string;
  requiresConfirmation: boolean;
  source: 'builtin' | 'marketplace';
};

export type McpAvailableToolGroup = {
  category: string;
  tools: McpAvailableTool[];
};

export type McpAvailableToolsResponse = {
  role: string;
  groups: McpAvailableToolGroup[];
};

const normalizeToolCategory = (category: string) => {
  const normalized = category.trim();
  const roleCategoryPrefix =
    'com.streamlain.settings.workspace.roles.category.';
  if (normalized.startsWith(roleCategoryPrefix)) {
    return normalized.slice(roleCategoryPrefix.length);
  }
  if (
    normalized === 'workspace' ||
    normalized.startsWith('com.streamlain.settings.workspace')
  ) {
    return 'workspace';
  }
  return normalized;
};

const toolCategoryLabelKey = (category: string) =>
  `com.streamlain.settings.workspace.mcp-access.tools.category.${normalizeToolCategory(category)}`;

const normalizeToolName = (name: string) => name.trim().replace(/_/g, '-');

const toolNameLabelKey = (name: string) =>
  `com.streamlain.settings.workspace.mcp-access.tools.name.${normalizeToolName(name)}`;

const formatToolCategory = (category: string) =>
  normalizeToolCategory(category)
    .split(/[._-]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || category;

const formatToolName = (name: string) =>
  name
    .trim()
    .split(/[_-]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || name;

const workspacePermissionRoleLabelKey = (role: string) =>
  `com.streamlain.settings.workspace.permissions.role.${role.toLowerCase()}`;

export const translateOptional = (
  t: ReturnType<typeof useI18n>,
  key: string,
  fallback: string,
  options?: Record<string, string>
) => {
  const entry = (
    t as Record<
      string,
      ((options?: Record<string, string>) => string) | undefined
    >
  )[key];
  const translated = entry?.(options);
  if (
    !translated ||
    translated === key ||
    translated.startsWith('com.streamlain.')
  ) {
    return fallback;
  }
  return translated;
};

export const formatWorkspaceRole = (
  t: ReturnType<typeof useI18n>,
  role: string
) => translateOptional(t, workspacePermissionRoleLabelKey(role), role);

export const McpTokenRow = ({
  token,
  avatarUrl,
  onRevoke,
}: {
  token: McpToken;
  avatarUrl?: string | null;
  onRevoke?: (id: string) => Promise<void>;
}) => {
  const t = useI18n();
  const [revoking, setRevoking] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!onRevoke) return;
    setRevoking(true);
    try {
      await onRevoke(token.id);
    } finally {
      setRevoking(false);
    }
  }, [onRevoke, token.id]);

  const createdDate = new Date(token.createdAt).toLocaleDateString();
  const expiresText = token.expiresAt
    ? (t['com.streamlain.settings.workspace.mcp-access.meta.expires']?.({
        date: new Date(token.expiresAt).toLocaleDateString(),
      }) ?? `Expires ${new Date(token.expiresAt).toLocaleDateString()}`)
    : (t[
        'com.streamlain.settings.workspace.mcp-access.meta.never-expires'
      ]?.() ?? 'Never expires');
  const createdText =
    t['com.streamlain.settings.workspace.mcp-access.meta.created-by']?.({
      date: createdDate,
      creator: token.createdByName ?? token.createdBy,
    }) ?? `Created ${createdDate} by ${token.createdByName ?? token.createdBy}`;
  const displayName =
    token.userName ??
    t['com.streamlain.settings.workspace.mcp-access.unknown-user']?.() ??
    'Unknown';
  const roleLabel = formatWorkspaceRole(t, token.userRole);

  return (
    <SettingsEntityCard>
      <div className={styles.tokenRow}>
        <div className={styles.tokenIdentity}>
          <PersonRow
            person={{
              id: token.userId,
              name: displayName,
              email: token.userEmail,
              avatarUrl,
            }}
            variant="row"
            avatarSize={40}
            subtitle={token.userEmail}
            badges={<SettingsBadge variant="soft">{roleLabel}</SettingsBadge>}
            showTooltips
          />
          <code className={styles.tokenChip}>{token.maskedToken}</code>
          <div className={styles.metaRow}>
            <span>{createdText}</span>
            <span className={styles.metaSeparator}>·</span>
            <span>{expiresText}</span>
          </div>
        </div>
        {onRevoke ? (
          <div className={styles.tokenActions}>
            <SettingsConfirmButton
              label={
                t['com.streamlain.settings.workspace.mcp-access.revoke']?.() ??
                'Revoke'
              }
              confirmLabel={
                t[
                  'com.streamlain.settings.workspace.mcp-access.confirm-revoke'
                ]?.() ?? 'Confirm Revoke'
              }
              loadingLabel={
                t[
                  'com.streamlain.settings.workspace.mcp-access.revoking'
                ]?.() ?? 'Revoking...'
              }
              cancelLabel={t['Cancel']?.() ?? 'Cancel'}
              onConfirm={() => void handleConfirm()}
              variant="error"
              disabled={revoking}
              loading={revoking}
              dataTestId={`mcp-access-revoke-${token.id}`}
            />
          </div>
        ) : null}
      </div>
    </SettingsEntityCard>
  );
};

export const GroupedToolPolicyList = ({
  role,
  groups,
  loading,
  error,
  disabledCategories,
  disabledTools,
  onToggleCategory,
  onToggleTool,
  title,
  selectionNote,
  testId = 'mcp-access-tool-preview',
}: {
  role: string;
  groups?: McpAvailableToolGroup[];
  loading: boolean;
  error?: string | null;
  disabledCategories?: ReadonlySet<string>;
  disabledTools?: ReadonlySet<string>;
  onToggleCategory?: (category: string) => void;
  onToggleTool?: (toolName: string, category: string) => void;
  title?: string;
  selectionNote?: string;
  testId?: string;
}) => {
  const t = useI18n();
  const [expandedToolCategories, setExpandedToolCategories] = useState<
    Set<string>
  >(() => new Set());

  useEffect(() => {
    setExpandedToolCategories(new Set());
  }, [groups, role]);

  const translate = useCallback(
    (key: string, fallback: string, options?: Record<string, string>) =>
      translateOptional(t, key, fallback, options),
    [t]
  );

  const handleToggleToolCategory = useCallback((category: string) => {
    setExpandedToolCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const roleLabel = formatWorkspaceRole(t, role);
  const selectable = Boolean(onToggleCategory || onToggleTool);

  return (
    <div className={styles.toolPreview} data-testid={testId}>
      <div className={styles.toolPreviewTitle}>
        {title ??
          t[
            'com.streamlain.settings.workspace.mcp-access.tools.available-for'
          ]?.({ role: roleLabel }) ??
          `Available tools for ${roleLabel}:`}
      </div>
      {loading ? (
        <div className={styles.toolPreviewNote}>
          {translate(
            'com.streamlain.settings.workspace.mcp-access.tools.loading',
            'Loading tools...'
          )}
        </div>
      ) : error ? (
        <div className={styles.toolPreviewNote}>
          {translate(
            'com.streamlain.settings.workspace.mcp-access.tools.load-error',
            'Could not load tools.'
          )}
        </div>
      ) : groups?.length ? (
        <>
          {selectable ? (
            <div className={styles.toolPreviewNote}>
              {selectionNote ??
                translate(
                  'com.streamlain.settings.workspace.mcp-access.tools.selection-note',
                  'Disable groups or individual tools for this token.'
                )}
            </div>
          ) : null}
          <div className={styles.toolGroupGrid}>
            {[0, 1].map(columnIndex => (
              <div key={columnIndex} className={styles.toolGroupColumn}>
                {groups
                  .filter((_, index) => index % 2 === columnIndex)
                  .map(group => {
                    const labelFallback = formatToolCategory(group.category);
                    const groupLabel = translate(
                      toolCategoryLabelKey(group.category),
                      labelFallback
                    );
                    const expanded = expandedToolCategories.has(group.category);
                    const categoryDisabled =
                      disabledCategories?.has(group.category) ?? false;
                    const enabledToolCount = categoryDisabled
                      ? 0
                      : group.tools.filter(
                          tool => !(disabledTools?.has(tool.name) ?? false)
                        ).length;
                    const groupAllEnabled =
                      group.tools.length > 0 &&
                      enabledToolCount === group.tools.length;
                    const groupPartiallyEnabled =
                      enabledToolCount > 0 &&
                      enabledToolCount < group.tools.length;
                    const groupDisabled = enabledToolCount === 0;
                    return (
                      <section
                        key={group.category}
                        className={styles.toolGroup}
                        data-disabled={groupDisabled ? 'true' : 'false'}
                      >
                        <div className={styles.toolGroupHeader}>
                          <button
                            type="button"
                            className={styles.toolGroupExpandButton}
                            aria-expanded={expanded}
                            onClick={() =>
                              handleToggleToolCategory(group.category)
                            }
                          >
                            <span
                              className={styles.toolGroupChevron}
                              data-expanded={expanded ? 'true' : 'false'}
                            >
                              <ArrowDownSmallIcon />
                            </span>
                            <span className={styles.toolGroupTitle}>
                              {groupLabel}
                            </span>
                            <span className={styles.toolGroupCount}>
                              {group.tools.length}
                            </span>
                          </button>
                          {onToggleCategory ? (
                            <label
                              className={styles.toolPolicyToggle}
                              title={
                                groupAllEnabled
                                  ? translate(
                                      'com.streamlain.settings.workspace.mcp-access.tools.disable-group',
                                      'Disable group'
                                    )
                                  : translate(
                                      'com.streamlain.settings.workspace.mcp-access.tools.enable-group',
                                      'Enable group'
                                    )
                              }
                            >
                              <input
                                type="checkbox"
                                className={styles.toolPolicyCheckbox}
                                checked={groupAllEnabled}
                                data-mixed={
                                  groupPartiallyEnabled ? 'true' : undefined
                                }
                                onChange={() =>
                                  onToggleCategory(group.category)
                                }
                                aria-label={
                                  groupAllEnabled
                                    ? translate(
                                        'com.streamlain.settings.workspace.mcp-access.tools.disable-group',
                                        'Disable group'
                                      )
                                    : translate(
                                        'com.streamlain.settings.workspace.mcp-access.tools.enable-group',
                                        'Enable group'
                                      )
                                }
                              />
                              <span
                                className={styles.toolPolicyBox}
                                aria-hidden="true"
                              />
                            </label>
                          ) : null}
                        </div>
                        {expanded && (
                          <div className={styles.toolChipList}>
                            {group.tools.map(tool => {
                              const toolLabel = translate(
                                toolNameLabelKey(tool.name),
                                formatToolName(tool.name)
                              );
                              const toolDisabled =
                                categoryDisabled ||
                                (disabledTools?.has(tool.name) ?? false);
                              return onToggleTool ? (
                                <label
                                  key={`${tool.source}:${tool.name}`}
                                  className={styles.toolChip}
                                  data-disabled={
                                    toolDisabled ? 'true' : 'false'
                                  }
                                  title={tool.description}
                                >
                                  <input
                                    type="checkbox"
                                    className={styles.toolPolicyCheckbox}
                                    checked={!toolDisabled}
                                    onChange={() =>
                                      onToggleTool(tool.name, group.category)
                                    }
                                    aria-label={
                                      toolDisabled
                                        ? translate(
                                            'com.streamlain.settings.workspace.mcp-access.tools.enable-tool',
                                            'Enable tool'
                                          )
                                        : translate(
                                            'com.streamlain.settings.workspace.mcp-access.tools.disable-tool',
                                            'Disable tool'
                                          )
                                    }
                                  />
                                  <span
                                    className={styles.toolPolicyBox}
                                    aria-hidden="true"
                                  />
                                  <code className={styles.toolChipCode}>
                                    {toolLabel}
                                  </code>
                                </label>
                              ) : (
                                <code
                                  key={`${tool.source}:${tool.name}`}
                                  className={styles.toolChip}
                                  title={tool.description}
                                >
                                  {toolLabel}
                                </code>
                              );
                            })}
                          </div>
                        )}
                      </section>
                    );
                  })}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className={styles.toolPreviewNote}>
          {translate(
            'com.streamlain.settings.workspace.mcp-access.tools.empty',
            'No tools available for this member.'
          )}
        </div>
      )}
    </div>
  );
};

/** Backward-compatible product name for the MCP access adapter. */
export const McpToolsPreview = GroupedToolPolicyList;
